import React, { useState } from 'react';

import {
    Box,
    Checkbox,
    Chip,
    FormControl,
    FormControlLabel,
    FormHelperText,
    IconButton,
    InputAdornment,
    ListItemText,
    makeStyles,
    MenuItem,
    Radio,
    RadioGroup,
    TextField,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import EventIcon from '@material-ui/icons/Event';
import RemoveIcon from '@material-ui/icons/Remove';
import { Alert } from '@material-ui/lab';
import { format, parseISO } from 'date-fns';
import { useFormikContext } from 'formik';
import i18n from 'i18n-js';

import { StaffReservationChannel } from 'api/controllers/reservations';
import { DateTimePicker } from 'components/shared';
import { theme } from 'config/theme';
import {
    DATE_FORMAT,
    DURATION_PRESETS,
    formatDuration,
    formatReservationDate,
    getTimeSlots,
    isPastSlot,
    MAX_DURATION_MINUTES,
    MAX_GUESTS,
    MAX_GUESTS_CHIP,
    RESERVATION_MAX_LENGTH,
    ReservationFormErrors,
    ReservationFormValues,
    SLOT_MINUTES,
} from 'helpers/reservationHelper';
import { useAllergens } from '../hooks';

const OTHER_ALLERGY = 'other';
const STAFF_CHANNELS: StaffReservationChannel[] = ['Phone', 'Email'];
const TIME_SLOTS = getTimeSlots();
const GUEST_CHIPS = Array.from({ length: MAX_GUESTS_CHIP }, (_, index) => index + 1);
const MENU_PROPS = { PaperProps: { style: { maxHeight: 300 } } };

const useStyles = makeStyles({
    caption: {
        fontSize: 10,
        letterSpacing: 1.25,
        textTransform: 'uppercase',
        color: theme.palette.common.black,
        margin: '16px 0 8px',
        '&:first-child': {
            marginTop: 0,
        },
    },
    row: {
        display: 'flex',
        gap: 12,
        marginBottom: 8,
    },
    passClicks: {
        pointerEvents: 'none',
    },
    dateIcon: {
        position: 'absolute',
        right: 12,
        top: '50%',
    },
    label: {
        fontSize: 12,
        color: theme.palette.text.secondary,
        marginBottom: 4,
    },
    chips: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 8,
        marginBottom: 8,
    },
    hint: {
        fontSize: 12,
        color: theme.palette.text.disabled,
    },
    error: {
        fontSize: 12,
        color: theme.palette.error.main,
        marginBottom: 8,
    },
    channel: {
        fontSize: 14,
        marginBottom: 12,
    },
    notice: {
        marginBottom: 12,
        fontSize: 16,
        color: theme.palette.text.primary,
        borderColor: theme.palette.warning.main,
        backgroundColor: theme.palette.common.white,
        '& .MuiAlert-icon': {
            color: theme.palette.warning.main,
        },
    },
    exactGuestsLabel: {
        maxWidth: 'calc((100% - 88px) / 0.75)',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
});

interface ReservationFormFieldsProps {
    channelLine: string | null;
    defaultDurationMinutes: number | null;
    notice: string | null;
    duplicateWarning: string | null;
    isOutsideOpeningHours: boolean;
}

export default function ReservationFormFields({
    channelLine,
    defaultDurationMinutes,
    notice,
    duplicateWarning,
    isOutsideOpeningHours,
}: ReservationFormFieldsProps) {
    const classes = useStyles();
    const [showDatePicker, setShowDatePicker] = useState(false);
    const allergens = useAllergens();
    const {
        values,
        errors,
        touched,
        submitCount,
        setFieldValue,
        setValues,
        handleChange,
        handleBlur,
    } = useFormikContext<ReservationFormValues>();

    const errorOf = (field: keyof ReservationFormErrors) =>
        touched[field] || submitCount > 0 ? errors[field] : undefined;

    const numberOrNull = (value: string) => (value === '' ? null : Number(value));

    const stepExactGuests = (delta: number) =>
        setFieldValue(
            'guestsCount',
            Math.max(MAX_GUESTS_CHIP + 1, (values.guestsCount ?? MAX_GUESTS_CHIP) + delta)
        );

    const textField = (
        name: 'firstName' | 'lastName' | 'phoneNumber' | 'email',
        label: string,
        isRequired = false
    ) => (
        <TextField
            variant="filled"
            fullWidth
            required={isRequired}
            name={name}
            label={label}
            value={values[name]}
            onChange={handleChange}
            onBlur={handleBlur}
            error={!!errorOf(name)}
            helperText={errorOf(name)}
            inputProps={{ maxLength: RESERVATION_MAX_LENGTH[name] }}
        />
    );

    const now = new Date();
    const selectedAllergies = values.allergenIds.concat(
        values.hasOtherAllergies ? [OTHER_ALLERGY] : []
    );
    const timeHelper =
        errorOf('time') ??
        (isOutsideOpeningHours ? i18n.t('reservations.outsideOpeningHours') : undefined);

    return (
        <>
            {notice && (
                <Alert severity="warning" variant="outlined" className={classes.notice}>
                    {notice}
                </Alert>
            )}

            <div className={classes.caption}>{i18n.t('reservations.sections.reservation')}</div>
            <div className={classes.row}>
                <Box position="relative" width="100%">
                    <TextField
                        id="date-time-picker"
                        variant="filled"
                        fullWidth
                        required
                        label={i18n.t('reservations.fields.date')}
                        value={
                            values.date ? formatReservationDate(values.date, 'EEE, dd.MM.yyyy') : ''
                        }
                        onClick={() => setShowDatePicker(true)}
                        onKeyDown={(event) => event.key === 'Enter' && setShowDatePicker(true)}
                        error={!!errorOf('date')}
                        helperText={errorOf('date')}
                        InputLabelProps={{ className: classes.passClicks }}
                        InputProps={{
                            readOnly: true,
                            endAdornment: (
                                <InputAdornment
                                    position="end"
                                    className={`${classes.passClicks} ${classes.dateIcon}`}
                                >
                                    <EventIcon />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <DateTimePicker
                        key={values.date}
                        showButtons
                        hideTimeSelect
                        futureOnly
                        alignLeft
                        date={values.date ? parseISO(values.date) : new Date()}
                        onSubmit={(date: Date) => setFieldValue('date', format(date, DATE_FORMAT))}
                        isOpen={showDatePicker}
                        onClose={() => setShowDatePicker(false)}
                    />
                </Box>
                <TextField
                    select
                    variant="filled"
                    fullWidth
                    required
                    name="time"
                    label={i18n.t('reservations.fields.time')}
                    value={values.time}
                    onChange={handleChange}
                    error={!!errorOf('time')}
                    helperText={timeHelper}
                    FormHelperTextProps={
                        errorOf('time')
                            ? undefined
                            : { style: { color: theme.palette.warning.main } }
                    }
                    SelectProps={{ MenuProps: MENU_PROPS }}
                >
                    {TIME_SLOTS.map((slot) => (
                        <MenuItem
                            key={slot}
                            value={slot}
                            disabled={isPastSlot(values.date, slot, now)}
                        >
                            {slot}
                        </MenuItem>
                    ))}
                </TextField>
            </div>

            <div className={classes.label}>{i18n.t('reservations.fields.guests')} *</div>
            <div className={classes.chips}>
                {GUEST_CHIPS.map((count) => (
                    <Chip
                        key={count}
                        clickable
                        label={count}
                        color={
                            !values.isMoreThanMaxGuests && values.guestsCount === count
                                ? 'primary'
                                : 'default'
                        }
                        onClick={() => {
                            setValues({
                                ...values,
                                isMoreThanMaxGuests: false,
                                guestsCount: count,
                            });
                        }}
                    />
                ))}
                <Chip
                    clickable
                    label={`${MAX_GUESTS_CHIP}+`}
                    color={values.isMoreThanMaxGuests ? 'primary' : 'default'}
                    onClick={() => {
                        if (!values.isMoreThanMaxGuests) {
                            setValues({
                                ...values,
                                isMoreThanMaxGuests: true,
                                guestsCount: null,
                            });
                        }
                    }}
                />
            </div>
            {values.isMoreThanMaxGuests ? (
                <div className={classes.row}>
                    <TextField
                        variant="filled"
                        fullWidth
                        required
                        type="number"
                        name="guestsCount"
                        label={i18n.t('reservations.fields.exactGuests')}
                        value={values.guestsCount ?? ''}
                        onChange={(e) => setFieldValue('guestsCount', numberOrNull(e.target.value))}
                        onBlur={handleBlur}
                        error={!!errorOf('guestsCount')}
                        helperText={errorOf('guestsCount')}
                        inputProps={{ min: MAX_GUESTS_CHIP + 1, max: MAX_GUESTS, step: 1 }}
                        InputLabelProps={{ shrink: true, className: classes.exactGuestsLabel }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={() => stepExactGuests(-1)}>
                                        <RemoveIcon />
                                    </IconButton>
                                    <IconButton size="small" onClick={() => stepExactGuests(1)}>
                                        <AddIcon />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                </div>
            ) : (
                errorOf('guestsCount') && (
                    <div className={classes.error}>{errorOf('guestsCount')}</div>
                )
            )}

            <div className={classes.label}>{i18n.t('reservations.fields.duration')} *</div>
            <div className={classes.chips}>
                {DURATION_PRESETS.map((minutes) => (
                    <Chip
                        key={minutes}
                        clickable
                        label={formatDuration(minutes)}
                        color={
                            !values.isCustomDuration && values.durationMinutes === minutes
                                ? 'primary'
                                : 'default'
                        }
                        onClick={() => {
                            setValues({
                                ...values,
                                isCustomDuration: false,
                                durationMinutes: minutes,
                            });
                        }}
                    />
                ))}
                <Chip
                    clickable
                    label={i18n.t('reservations.other')}
                    color={values.isCustomDuration ? 'primary' : 'default'}
                    onClick={() => setFieldValue('isCustomDuration', true)}
                />
                {defaultDurationMinutes !== null && (
                    <span className={classes.hint}>
                        {i18n.t('reservations.duration.defaultFromSettings', {
                            duration: formatDuration(defaultDurationMinutes),
                        })}
                    </span>
                )}
            </div>
            {values.isCustomDuration && (
                <div className={classes.row}>
                    <TextField
                        variant="filled"
                        fullWidth
                        required
                        type="number"
                        name="durationMinutes"
                        label={i18n.t('reservations.fields.duration')}
                        value={values.durationMinutes ?? ''}
                        onChange={(e) =>
                            setFieldValue('durationMinutes', numberOrNull(e.target.value))
                        }
                        onBlur={handleBlur}
                        error={!!errorOf('durationMinutes')}
                        helperText={errorOf('durationMinutes')}
                        inputProps={{
                            min: SLOT_MINUTES,
                            max: MAX_DURATION_MINUTES,
                            step: SLOT_MINUTES,
                        }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    {i18n.t('reservations.duration.unitMinutes')}
                                </InputAdornment>
                            ),
                        }}
                    />
                </div>
            )}

            <div className={classes.caption}>{i18n.t('reservations.sections.client')}</div>
            {channelLine !== null ? (
                <>
                    <div className={classes.label}>{i18n.t('reservations.columns.channel')}</div>
                    <div className={classes.channel}>{channelLine}</div>
                </>
            ) : (
                <FormControl error={!!errorOf('channel')} className={classes.channel}>
                    <div className={classes.label}>{i18n.t('reservations.fields.channel')} *</div>
                    <RadioGroup row name="channel" value={values.channel} onChange={handleChange}>
                        {STAFF_CHANNELS.map((channel) => (
                            <FormControlLabel
                                key={channel}
                                value={channel}
                                control={<Radio color="primary" />}
                                label={i18n.t(`reservations.channels.${channel}`)}
                            />
                        ))}
                    </RadioGroup>
                    {errorOf('channel') && <FormHelperText>{errorOf('channel')}</FormHelperText>}
                </FormControl>
            )}
            <div className={classes.row}>
                {textField('firstName', i18n.t('reservations.fields.firstName'))}
                {textField('lastName', i18n.t('reservations.fields.lastName'), true)}
            </div>
            <div className={classes.row}>
                {textField('phoneNumber', i18n.t('reservations.fields.phone'))}
                {textField('email', i18n.t('reservations.fields.email'))}
            </div>
            <div className={classes.hint}>{i18n.t('reservations.contactHelper')}</div>
            {duplicateWarning && (
                <Box mt={1}>
                    <Alert severity="warning">{duplicateWarning}</Alert>
                </Box>
            )}

            <div className={classes.caption}>{i18n.t('reservations.sections.details')}</div>
            <div className={classes.row}>
                <TextField
                    select
                    variant="filled"
                    fullWidth
                    label={i18n.t('reservations.fields.allergies')}
                    value={selectedAllergies}
                    onChange={(e) => {
                        const selected = e.target.value as unknown as string[];
                        setValues({
                            ...values,
                            allergenIds: selected.filter((id) => id !== OTHER_ALLERGY),
                            hasOtherAllergies: selected.includes(OTHER_ALLERGY),
                        });
                    }}
                    SelectProps={{
                        multiple: true,
                        MenuProps: { ...MENU_PROPS, getContentAnchorEl: null },
                        renderValue: () =>
                            allergens
                                .filter((allergen) => values.allergenIds.includes(allergen.id))
                                .map((allergen) => allergen.name)
                                .concat(
                                    values.hasOtherAllergies ? [i18n.t('reservations.other')] : []
                                )
                                .join(', '),
                    }}
                >
                    {allergens.map((allergen) => (
                        <MenuItem key={allergen.id} value={allergen.id}>
                            <Checkbox checked={values.allergenIds.includes(allergen.id)} />
                            <ListItemText primary={allergen.name} />
                        </MenuItem>
                    ))}
                    <MenuItem value={OTHER_ALLERGY}>
                        <Checkbox checked={values.hasOtherAllergies} />
                        <ListItemText primary={i18n.t('reservations.other')} />
                    </MenuItem>
                </TextField>
            </div>
            {values.hasOtherAllergies && (
                <div className={classes.row}>
                    <TextField
                        variant="filled"
                        fullWidth
                        name="otherAllergies"
                        label={i18n.t('reservations.other')}
                        value={values.otherAllergies}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={!!errorOf('otherAllergies')}
                        helperText={errorOf('otherAllergies')}
                        inputProps={{ maxLength: RESERVATION_MAX_LENGTH.otherAllergies }}
                    />
                </div>
            )}
            <div className={classes.row}>
                <TextField
                    variant="filled"
                    fullWidth
                    multiline
                    name="specialRequests"
                    label={i18n.t('reservations.fields.specialRequests')}
                    value={values.specialRequests}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={!!errorOf('specialRequests')}
                    helperText={errorOf('specialRequests')}
                    inputProps={{ maxLength: RESERVATION_MAX_LENGTH.specialRequests }}
                />
            </div>
        </>
    );
}
